import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { BookOpen, Plus, Search, LogOut, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import SourceCard from "@/components/sources/SourceCard";
import AddSourceModal from "@/components/sources/AddSourceModal";

const POLL_INTERVAL_MS = 7000;

export default function SourceLibrary() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const fetchSources = async (q = "") => {
    try {
      const url = q ? `${API}/sources/search?q=${encodeURIComponent(q)}` : `${API}/sources`;
      const resp = await axios.get(url, { withCredentials: true });
      setSources(resp.data);
    } catch {
      toast.error("Failed to load sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSources(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchSources(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const hasProcessing = sources.some(s => s.status === "processing");
    if (!hasProcessing) return;
    const interval = setInterval(() => fetchSources(search), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [sources, search]);

  const _handleSourceError = (err) => {
    const detail = err.response?.data?.detail;
    if (err.response?.status === 403 && detail?.error === "source_limit") {
      toast.error(detail.message, {
        action: { label: "Upgrade", onClick: () => navigate("/settings") },
        duration: 8000,
      });
      return true;
    }
    return false;
  };

  const handleAddLink = async ({ title, url: linkUrl }) => {
    try {
      await axios.post(`${API}/sources`, {
        title, source_type: "link", url: linkUrl, content: linkUrl,
      }, { withCredentials: true });
      toast.success("Source added");
      fetchSources(search);
    } catch (err) {
      if (!_handleSourceError(err)) toast.error("Failed to add source");
    }
  };

  const handleAddNote = async ({ title, content }) => {
    try {
      await axios.post(`${API}/sources`, {
        title, source_type: "note", content,
      }, { withCredentials: true });
      toast.success("Source added");
      fetchSources(search);
    } catch (err) {
      if (!_handleSourceError(err)) toast.error("Failed to add source");
    }
  };

  const handleUploadFile = async ({ title, file }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      await axios.post(`${API}/sources/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      fetchSources(search);
    } catch (err) {
      if (!_handleSourceError(err)) toast.error("Failed to upload file");
    }
  };

  const handleDelete = async (sourceId) => {
    try {
      await axios.delete(`${API}/sources/${sourceId}`, { withCredentials: true });
      setSources(prev => prev.filter(s => s.source_id !== sourceId));
      toast.success("Source deleted");
    } catch {
      toast.error("Failed to delete source");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen mesh-bg">
      {/* Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 surface-nav">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/dashboard")}>
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-heading text-xs">S</span>
              </div>
              <span className="font-heading text-foreground text-base">Shadow</span>
            </div>
            <span className="text-border">|</span>
            <span className="text-sm text-muted-foreground font-medium">Knowledge Base</span>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full h-8 gap-2 px-2.5">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-[10px] bg-secondary text-muted-foreground">{user?.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-muted-foreground hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl p-1.5">
                <DropdownMenuItem className="text-xs text-muted-foreground rounded-lg">{user?.email}</DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg">
                  <Link to="/privacy" className="cursor-pointer">Privacy Policy</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg">
                  <Link to="/terms" className="cursor-pointer">Terms of Service</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="rounded-lg text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-20 px-6 max-w-6xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-[28px] text-foreground tracking-tight">
              Knowledge Base
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Add docs, links, and notes that Shadow can use during your meetings.
            </p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="rounded-full h-9 gap-1.5 text-sm px-5">
            <Plus className="w-3.5 h-3.5" />
            Add Source
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="surface-input flex items-center gap-2 h-10 px-4 rounded-xl max-w-md">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-foreground outline-none"
              placeholder="Search sources by title or topic..."
            />
          </div>
        </div>

        {/* Sources list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-border border-t-foreground rounded-full animate-spin" />
          </div>
        ) : sources.length === 0 ? (
          <div className="surface-card rounded-2xl p-12 text-center">
            <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-heading text-foreground text-base mb-1">No sources yet</h3>
            <p className="text-muted-foreground text-sm mb-5">
              Add docs, links, or notes to build your source library.
            </p>
            <Button onClick={() => setShowAdd(true)} className="rounded-full h-9 gap-1.5 text-sm px-5">
              <Plus className="w-3.5 h-3.5" />
              Add your first source
            </Button>
          </div>
        ) : (
          <div className="grid gap-3">
            {sources.map((source, i) => (
              <SourceCard
                key={source.source_id}
                source={source}
                onDelete={handleDelete}
                delay={i * 0.04}
              />
            ))}
          </div>
        )}
      </main>

      <AddSourceModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onAddLink={handleAddLink}
        onAddNote={handleAddNote}
        onUploadFile={handleUploadFile}
      />
    </div>
  );
}
